"use client";

import { c as _c } from "react/compiler-runtime";
import * as PopoverPrimitives from "@radix-ui/react-popover";
import React, { useEffect, useRef, useState } from "react";
import { RiCalendar2Fill } from "react-icons/ri";
import { Calendar as CalendarPrimitive } from "@/components/ui/calendar";
import { InputProps } from "@/components/ui/input";
import { pickedDay } from "@/lib/clinic-clock";
import { fromDateOnlyString } from "@/lib/format-data";
import { cx, hasErrorInput } from "@/lib/tailwind-utils";
interface DateOfBirthInputProps extends Omit<InputProps, "onChange"> {
  value?: string; // Expected in YYYY-MM-DD format
  onChange?: (value: string | null) => void; // Returns YYYY-MM-DD string instead of Date
  onValidityChange?: (isValid: boolean) => void;
  className?: string;
  maxDate?: Date;
  minDate?: Date;
}
const DateOfBirthInput = React.forwardRef<HTMLInputElement, DateOfBirthInputProps>((t0, ref) => {
  const $ = _c(82);
  const {
    value,
    onChange,
    onValidityChange,
    className,
    maxDate,
    minDate,
    ...props
  } = t0;
  const parseValue = _temp;
  let t1;
  if ($[0] !== value) {
    t1 = () => parseValue(value).month;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [month, setMonth] = useState(t1);
  let t2;
  if ($[2] !== value) {
    t2 = () => parseValue(value).day;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [day, setDay] = useState(t2);
  let t3;
  if ($[4] !== value) {
    t3 = () => parseValue(value).year;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [year, setYear] = useState(t3);
  const [calendarOpen, setCalendarOpen] = useState(false);
  let t4;
  if ($[6] !== value) {
    t4 = () => {
      if (value) {
        const date = fromDateOnlyString(value);
        return isNaN(date.getTime()) ? undefined : date;
      }
    };
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [calendarMonth, setCalendarMonth] = useState(t4);
  const monthInputRef = useRef(null);
  const dayInputRef = useRef(null);
  const yearInputRef = useRef(null);
  const containerRef = useRef(null);
  const isUpdatingRef = useRef(false);
  let t5;
  if ($[8] !== setCalendarMonth || $[9] !== value) {
    t5 = () => {
      if (isUpdatingRef.current) {
        isUpdatingRef.current = false;
        return;
      }
      const parsed = parseValue(value);
      setMonth(parsed.month);
      setDay(parsed.day);
      setYear(parsed.year);
      if (value) {
        const date_0 = fromDateOnlyString(value);
        if (!isNaN(date_0.getTime())) {
          setCalendarMonth(date_0);
        }
      }
    };
    $[8] = setCalendarMonth;
    $[9] = value;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== value) {
    t6 = [value];
    $[11] = value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== calendarOpen) {
    t7 = [calendarOpen];
    $[13] = calendarOpen;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(() => {
    if (calendarOpen) {
      const dateToShow = currentDate || new Date();
      setCalendarMonth(dateToShow);
    }
  }, t7);
  let t8;
  if ($[15] !== month.length) {
    t8 = () => {
      if (month.length === 2 && dayInputRef.current) {
        dayInputRef.current.focus();
      }
    };
    $[15] = month.length;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== month) {
    t9 = [month];
    $[17] = month;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  useEffect(t8, t9);
  let t10;
  if ($[19] !== day.length) {
    t10 = () => {
      if (day.length === 2 && yearInputRef.current) {
        yearInputRef.current.focus();
      }
    };
    $[19] = day.length;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== day) {
    t11 = [day];
    $[21] = day;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[23] !== day || $[24] !== maxDate || $[25] !== minDate || $[26] !== month || $[27] !== onChange || $[28] !== onValidityChange || $[29] !== value || $[30] !== year) {
    t12 = () => {
      const clearParent = () => {
        if (value === null || value === undefined || value === "") {
          return;
        }
        isUpdatingRef.current = true;
        onChange?.(null);
      };
      if (year.length === 4 && month.length === 2 && day.length === 2) {
        const yearNum = parseInt(year);
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        if (yearNum > 0 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
          const testDate = new Date(yearNum, monthNum - 1, dayNum);
          if (testDate.getFullYear() === yearNum && testDate.getMonth() === monthNum - 1 && testDate.getDate() === dayNum) {
            if (maxDate && testDate > maxDate) {
              onValidityChange?.(false);
              clearParent();
              return;
            }
            if (minDate && testDate < minDate) {
              onValidityChange?.(false);
              clearParent();
              return;
            }
            const formattedValue = `${year}-${month}-${day}`;
            onValidityChange?.(true);
            if (value !== formattedValue) {
              isUpdatingRef.current = true;
              onChange?.(formattedValue);
            }
          } else {
            onValidityChange?.(false);
            clearParent();
          }
        } else {
          onValidityChange?.(false);
          clearParent();
        }
      } else {
        if (month === "" && day === "" && year === "") {
          onValidityChange?.(false);
          clearParent();
        } else {
          onValidityChange?.(false);
        }
      }
    };
    t13 = [year, month, day, maxDate, minDate, value, onChange, onValidityChange];
    $[23] = day;
    $[24] = maxDate;
    $[25] = minDate;
    $[26] = month;
    $[27] = onChange;
    $[28] = onValidityChange;
    $[29] = value;
    $[30] = year;
    $[31] = t12;
    $[32] = t13;
  } else {
    t12 = $[31];
    t13 = $[32];
  }
  useEffect(t12, t13);
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = part => e => {
      const newValue = e.target.value.replace(/\D/g, "");
      if (part === "month") {
        if (newValue === "") {
          setMonth("");
        } else {
          const numValue = parseInt(newValue);
          if (numValue <= 12) {
            setMonth(newValue.slice(0, 2));
          }
        }
      } else {
        if (part === "day") {
          if (newValue === "") {
            setDay("");
          } else {
            const numValue_0 = parseInt(newValue);
            if (numValue_0 <= 31) {
              setDay(newValue.slice(0, 2));
            }
          }
        } else {
          if (part === "year") {
            setYear(newValue.slice(0, 4));
          }
        }
      }
    };
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const handleChange = t14;
  const handleKeyDown = part_0 => e_0 => {
    if (e_0.key === "Backspace") {
      const currentValue = part_0 === "month" ? month : part_0 === "day" ? day : year;
      if (currentValue === "") {
        e_0.preventDefault();
        if (part_0 === "day" && monthInputRef.current) {
          monthInputRef.current.focus();
        } else {
          if (part_0 === "year" && dayInputRef.current) {
            dayInputRef.current.focus();
          }
        }
      }
    }
  };
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e_1 => {
      e_1.preventDefault();
      const pastedText = e_1.clipboardData.getData("text");
      const dateMatch = pastedText.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
      if (dateMatch) {
        const [, m, d, y] = dateMatch;
        const monthNum_0 = parseInt(m);
        const dayNum_0 = parseInt(d);
        const yearNum_0 = parseInt(y);
        if (monthNum_0 >= 1 && monthNum_0 <= 12 && dayNum_0 >= 1 && dayNum_0 <= 31 && yearNum_0 > 0) {
          setMonth(m.padStart(2, "0"));
          setDay(d.padStart(2, "0"));
          setYear(y);
          setTimeout(() => yearInputRef.current?.focus(), 0);
        }
      } else {
        const numbers = pastedText.replace(/\D/g, "");
        if (numbers.length >= 8) {
          const m_0 = numbers.slice(0, 2);
          const d_0 = numbers.slice(2, 4);
          const y_0 = numbers.slice(4, 8);
          const monthNum_1 = parseInt(m_0);
          const dayNum_1 = parseInt(d_0);
          const yearNum_1 = parseInt(y_0);
          if (monthNum_1 >= 1 && monthNum_1 <= 12 && dayNum_1 >= 1 && dayNum_1 <= 31 && yearNum_1 > 0) {
            setMonth(m_0);
            setDay(d_0);
            setYear(y_0);
            setTimeout(() => yearInputRef.current?.focus(), 0);
          }
        }
      }
    };
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const handlePaste = t15;
  let t16;
  if ($[35] !== day || $[36] !== month) {
    t16 = part_1 => () => {
      if (part_1 === "month" && month.length === 1) {
        setMonth(month.padStart(2, "0"));
      }
      if (part_1 === "day" && day.length === 1) {
        setDay(day.padStart(2, "0"));
      }
    };
    $[35] = day;
    $[36] = month;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const handleBlur = t16;
  let t17;
  if ($[38] !== onChange || $[39] !== onValidityChange || $[40] !== setCalendarOpen) {
    t17 = date_1 => {
      if (date_1) {
        const dateStr = pickedDay(date_1);
        const parsed_0 = parseValue(dateStr);
        setMonth(parsed_0.month);
        setDay(parsed_0.day);
        setYear(parsed_0.year);
        isUpdatingRef.current = true;
        onChange?.(dateStr);
        onValidityChange?.(true);
        setCalendarOpen(false);
      }
    };
    $[38] = onChange;
    $[39] = onValidityChange;
    $[40] = setCalendarOpen;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const handleCalendarDateSelect = t17;
  let t18;
  bb0: {
    if (year.length === 4 && month.length === 2 && day.length === 2) {
      const yearNum_2 = parseInt(year);
      const monthNum_2 = parseInt(month);
      const dayNum_2 = parseInt(day);
      let t19;
      let testDate_0;
      if ($[42] !== dayNum_2 || $[43] !== monthNum_2 || $[44] !== yearNum_2) {
        testDate_0 = new Date(yearNum_2, monthNum_2 - 1, dayNum_2);
        t19 = testDate_0.getFullYear() === yearNum_2 && testDate_0.getMonth() === monthNum_2 - 1 && testDate_0.getDate() === dayNum_2;
        $[42] = dayNum_2;
        $[43] = monthNum_2;
        $[44] = yearNum_2;
        $[45] = t19;
        $[46] = testDate_0;
      } else {
        t19 = $[45];
        testDate_0 = $[46];
      }
      if (t19) {
        t18 = testDate_0;
        break bb0;
      }
    }
    let t19;
    if ($[47] !== value) {
      t19 = value ? fromDateOnlyString(value) : undefined;
      $[47] = value;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    t18 = t19;
  }
  const currentDate = t18;
  const {
    hasError
  } = props;
  let t19;
  if ($[49] !== day.length || $[50] !== month.length) {
    t19 = e_2 => {
      if ((e_2.target as HTMLElement).closest("input, button")) {
        return;
      }
      const target = month.length < 2 ? monthInputRef.current : day.length < 2 ? dayInputRef.current : yearInputRef.current;
      target?.focus();
    };
    $[49] = day.length;
    $[50] = month.length;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const handleContainerClick = t19;
  let t20;
  if ($[52] !== className) {
    t20 = cx("relative w-full", className);
    $[52] = className;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  const t21 = cx("relative flex min-w-0 cursor-text items-center overflow-hidden rounded-md border border-border bg-transparent px-2.5 py-2 text-body shadow-sm transition", "focus-within:border-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/70", hasError && hasErrorInput);
  let t22;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = handleChange("month");
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  const t23 = handleKeyDown("month");
  let t24;
  if ($[55] !== handleBlur) {
    t24 = handleBlur("month");
    $[55] = handleBlur;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== ref) {
    t25 = node => {
      monthInputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
    };
    $[57] = ref;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="mx-0.5 shrink-0 text-body text-foreground-muted">/</span>;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = handleChange("day");
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  const t28 = handleKeyDown("day");
  let t29;
  if ($[61] !== handleBlur) {
    t29 = handleBlur("day");
    $[61] = handleBlur;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className="mx-0.5 shrink-0 text-body text-foreground-muted">/</span>;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const t31 = "text";
  const t32 = "numeric";
  const t33 = "YYYY";
  const t34 = "w-[5ch] shrink-0 border-0 bg-transparent p-0 text-center text-body text-foreground outline-none placeholder:text-foreground-muted focus:outline-none focus:ring-0";
  let t35;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = handleChange("year");
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  const t36 = handleKeyDown("year");
  let t37;
  if ($[65] !== handlePaste || $[66] !== t35 || $[67] !== t36 || $[68] !== year) {
    t37 = <input type={t31} inputMode={t32} placeholder={t33} className={t34} value={year} onChange={t35} onKeyDown={t36} onPaste={handlePaste} maxLength={4} ref={yearInputRef} />;
    $[65] = handlePaste;
    $[66] = t35;
    $[67] = t36;
    $[68] = year;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== setCalendarOpen) {
    t38 = e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      setCalendarOpen(true);
    };
    $[70] = setCalendarOpen;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <RiCalendar2Fill className="h-4 w-4" />;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] !== t38) {
    t40 = <PopoverPrimitives.Trigger asChild={true}><button type="button" onMouseDown={_temp2} onClick={t38} className="relative z-10 ml-auto flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center text-foreground-muted transition-colors hover:text-foreground-secondary focus:outline-none" aria-label="Open calendar">{t39}</button></PopoverPrimitives.Trigger>;
    $[73] = t38;
    $[74] = t40;
  } else {
    t40 = $[74];
  }
  let t41;
  if ($[75] !== calendarMonth || $[76] !== currentDate || $[77] !== handleCalendarDateSelect || $[78] !== maxDate || $[79] !== minDate || $[80] !== setCalendarMonth) {
    t41 = <PopoverPrimitives.Portal><PopoverPrimitives.Content align="start" side="bottom" sideOffset={5} className="z-50 w-auto rounded-md border border-border bg-background p-0 shadow-md outline-none"><CalendarPrimitive mode="single" selected={currentDate} onSelect={handleCalendarDateSelect} month={calendarMonth} onMonthChange={setCalendarMonth} toDate={maxDate} fromDate={minDate} enableMonthYearDropdowns={true} initialFocus={true} /></PopoverPrimitives.Content></PopoverPrimitives.Portal>;
    $[75] = calendarMonth;
    $[76] = currentDate;
    $[77] = handleCalendarDateSelect;
    $[78] = maxDate;
    $[79] = minDate;
    $[80] = setCalendarMonth;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  return <PopoverPrimitives.Root open={calendarOpen} onOpenChange={setCalendarOpen} modal={true}><PopoverPrimitives.Anchor asChild={true}><div className={t20} ref={containerRef}><div onClick={handleContainerClick} className={t21}><input type="text" inputMode="numeric" placeholder="MM" className="w-[3ch] shrink-0 border-0 bg-transparent p-0 text-center text-body text-foreground outline-none placeholder:text-foreground-muted focus:outline-none focus:ring-0" value={month} onChange={t22} onKeyDown={t23} onPaste={handlePaste} onBlur={t24} maxLength={2} ref={t25} />{t26}<input type="text" inputMode="numeric" placeholder="DD" className="w-[3ch] shrink-0 border-0 bg-transparent p-0 text-center text-body text-foreground outline-none placeholder:text-foreground-muted focus:outline-none focus:ring-0" value={day} onChange={t27} onKeyDown={t28} onPaste={handlePaste} onBlur={t29} maxLength={2} ref={dayInputRef} />{t30}{t37}{t40}</div></div></PopoverPrimitives.Anchor>{t41}</PopoverPrimitives.Root>;
});
DateOfBirthInput.displayName = "DateOfBirthInput";
export default DateOfBirthInput;
function _temp(val) {
  if (!val) {
    return {
      month: "",
      day: "",
      year: ""
    };
  }
  const parts = val.split("-");
  if (parts.length === 3) {
    return {
      month: parts[1] || "",
      day: parts[2] || "",
      year: parts[0] || ""
    };
  }
  return {
    month: "",
    day: "",
    year: ""
  };
}
function _temp2(e_3) {
  e_3.preventDefault();
  e_3.stopPropagation();
}